import type { ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

import type { FilterModel } from '@/components/lemmala-design-system/table/types'
import {
  extractColumnValue,
  normalizeAgGridDateValue,
  parseAgGridFilterModelDate,
} from '@/components/lemmala-design-system/table/utils/column-utils'
import { datePresetStart } from '@/components/lemmala-design-system/table/utils/date-range-filter'

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map((v) => String(v))
  if (value == null) return []
  return [String(value)]
}

/**
 * Hook that returns a predicate to evaluate whether a row matches a given FilterModel.
 * Supports 'set' filters by leveraging column valueGetter/field/dot-path extraction.
 */
export function useFilterModelEvaluator<TRow extends Record<string, unknown>>(
  columnDefs: ColDef<TRow>[],
) {
  return useMemo(() => {
    return (row: TRow, model?: FilterModel): boolean => {
      if (!model) return true

      for (const [fieldKey, entry] of Object.entries(model)) {
        const col = columnDefs.find((c) => (c.colId || c.field) === fieldKey)
        const rowValue = col ? extractColumnValue(col, row) : row[fieldKey]

        if (entry?.filterType === 'set' && Array.isArray(entry.values)) {
          const rowValues = toStringArray(rowValue)
          const hasMatch = rowValues.some((v) => entry.values.includes(v))
          if (!hasMatch) return false
          continue
        }

        if (entry?.filterType === 'date') {
          const rowDate = normalizeAgGridDateValue(rowValue)
          if (!rowDate) return false

          const fromDate =
            entry.type === 'inRange'
              ? parseAgGridFilterModelDate(entry.dateFrom)
              : datePresetStart(entry.type)
          const toDate =
            entry.type === 'inRange' ? parseAgGridFilterModelDate(entry.dateTo) : new Date()

          if (!fromDate || !toDate) return false
          if (rowDate.getTime() < fromDate.getTime() || rowDate.getTime() > toDate.getTime()) {
            return false
          }
        }
      }

      return true
    }
  }, [columnDefs])
}
